export type TrackingCarrierId = 'fedex' | 'usps' | 'dhl' | 'ups'

export interface TrackingCarrierOption {
  id: TrackingCarrierId
  name: string
  officialHost: string
}

export interface TrackingResolution {
  trackingNumber: string
  carrierId: TrackingCarrierId
  carrierName: string
  url: string
}

export type TrackingResult =
  | { ok: true; resolution: TrackingResolution }
  | { ok: false; error: string; requiresCarrier: boolean; trackingNumber: string }

export const trackingCarriers: readonly TrackingCarrierOption[] = [
  { id: 'fedex', name: 'FedEx', officialHost: 'www.fedex.com' },
  { id: 'usps', name: 'USPS', officialHost: 'tools.usps.com' },
  { id: 'dhl', name: 'DHL', officialHost: 'www.dhl.com' },
  { id: 'ups', name: 'UPS', officialHost: 'www.ups.com' },
]

const allowedTrackingCharacters = /^[A-Z0-9]+$/

export function normalizeTrackingNumber(input: string) {
  return input.trim().replace(/[\s-]+/g, '').toLocaleUpperCase('en-US')
}

export function validateTrackingNumber(input: string) {
  const trackingNumber = normalizeTrackingNumber(input)

  if (!trackingNumber) return 'Please enter a tracking number.'
  if (!allowedTrackingCharacters.test(trackingNumber)) {
    return 'Enter only the letters and numbers printed on the carrier label.'
  }
  if (trackingNumber.length < 7 || trackingNumber.length > 40) {
    return 'Please check the tracking number and try again.'
  }

  return null
}

export function detectTrackingCarrier(input: string): TrackingCarrierId | null {
  const trackingNumber = normalizeTrackingNumber(input)
  if (validateTrackingNumber(trackingNumber)) return null

  if (/^1Z[A-Z0-9]{16}$/.test(trackingNumber)) return 'ups'
  if (/^[A-Z]{2}\d{9}US$/.test(trackingNumber)) return 'usps'
  if (/^9[2345]\d{18,20}$/.test(trackingNumber)) return 'usps'
  if (/^82\d{8}$/.test(trackingNumber)) return 'usps'
  if (/^\d{15}$/.test(trackingNumber)) return 'fedex'
  if (/^\d{10}$/.test(trackingNumber)) return 'dhl'

  return null
}

export function buildOfficialTrackingUrl(carrierId: TrackingCarrierId, input: string) {
  const validationError = validateTrackingNumber(input)
  if (validationError) throw new Error(validationError)

  const trackingNumber = normalizeTrackingNumber(input)
  const carrier = trackingCarriers.find(({ id }) => id === carrierId)
  if (!carrier) throw new Error('Unsupported carrier.')

  let url: URL

  switch (carrierId) {
    case 'fedex':
      url = new URL('https://www.fedex.com/fedextrack/')
      url.searchParams.set('trknbr', trackingNumber)
      break
    case 'usps':
      url = new URL('https://tools.usps.com/go/TrackConfirmAction')
      url.searchParams.set('tLabels', trackingNumber)
      break
    case 'dhl':
      url = new URL('https://www.dhl.com/us-en/home/tracking/tracking-express.html')
      url.searchParams.set('submit', '1')
      url.searchParams.set('tracking-id', trackingNumber)
      break
    case 'ups':
      url = new URL('https://www.ups.com/track')
      url.searchParams.set('loc', 'en_US')
      url.searchParams.set('tracknum', trackingNumber)
      break
  }

  if (url.protocol !== 'https:' || url.hostname !== carrier.officialHost) {
    throw new Error('Unsafe tracking destination.')
  }

  return url.toString()
}

export function resolveTrackingRequest(
  input: string,
  selectedCarrier?: TrackingCarrierId,
): TrackingResult {
  const trackingNumber = normalizeTrackingNumber(input)
  const validationError = validateTrackingNumber(trackingNumber)

  if (validationError) {
    return { ok: false, error: validationError, requiresCarrier: false, trackingNumber }
  }

  const carrierId = detectTrackingCarrier(trackingNumber) ?? selectedCarrier
  if (!carrierId) {
    return {
      ok: false,
      error: 'Please select the carrier shown on your receipt or shipping label.',
      requiresCarrier: true,
      trackingNumber,
    }
  }

  const carrier = trackingCarriers.find(({ id }) => id === carrierId)
  if (!carrier) {
    return {
      ok: false,
      error: 'Please select a supported carrier.',
      requiresCarrier: true,
      trackingNumber,
    }
  }

  return {
    ok: true,
    resolution: {
      trackingNumber,
      carrierId,
      carrierName: carrier.name,
      url: buildOfficialTrackingUrl(carrierId, trackingNumber),
    },
  }
}
